/**
 * Automation Rule Field Metadata
 * 
 * This configuration defines all available fields, operators, and actions
 * for the rule builder UI.
 */

import type { FieldMetadata, ActionMetadata, RuleConditionField, RuleOperator, RuleActionType, RuleTemplate } from '@/types/automation-rules';

// ============================================================================
// Field Definitions
// ============================================================================

export const RULE_FIELDS: FieldMetadata[] = [
  // Employee Fields
  {
    field: 'employee.id',
    label: 'Employee ID',
    description: 'Specific employee identifier',
    category: 'employee',
    valueType: 'string',
    supportedOperators: ['equals', 'notEquals', 'in', 'notIn'],
    exampleValue: 'emp-123'
  },
  {
    field: 'employee.name',
    label: 'Employee Name',
    description: 'Employee full name',
    category: 'employee',
    valueType: 'string',
    supportedOperators: ['equals', 'notEquals', 'contains', 'notContains', 'in', 'notIn'],
    exampleValue: 'John Smith'
  },
  {
    field: 'employee.department',
    label: 'Department',
    description: 'Employee department',
    category: 'employee',
    valueType: 'string',
    supportedOperators: ['equals', 'notEquals', 'in', 'notIn'],
    exampleValue: 'Engineering'
  },
  {
    field: 'employee.role',
    label: 'Job Role',
    description: 'Employee job role or title',
    category: 'employee',
    valueType: 'string',
    supportedOperators: ['equals', 'notEquals', 'contains', 'notContains', 'in', 'notIn'],
    exampleValue: 'Senior Developer'
  },
  {
    field: 'employee.managerId',
    label: 'Manager',
    description: 'Employee\'s direct manager',
    category: 'employee',
    valueType: 'string',
    supportedOperators: ['equals', 'notEquals', 'in', 'notIn'],
    exampleValue: 'mgr-456'
  },

  // Timesheet Fields
  {
    field: 'timesheet.totalHours',
    label: 'Total Hours',
    description: 'Total hours submitted in timesheet',
    category: 'timesheet',
    valueType: 'number',
    supportedOperators: ['equals', 'notEquals', 'greaterThan', 'lessThan', 'greaterThanOrEqual', 'lessThanOrEqual'],
    exampleValue: 40
  },
  {
    field: 'timesheet.contractedHours',
    label: 'Contracted Hours',
    description: 'Employee\'s contracted weekly hours',
    category: 'timesheet',
    valueType: 'number',
    supportedOperators: ['equals', 'notEquals', 'greaterThan', 'lessThan', 'greaterThanOrEqual', 'lessThanOrEqual'],
    exampleValue: 40
  },
  {
    field: 'timesheet.overtimeHours',
    label: 'Overtime Hours',
    description: 'Hours worked beyond contracted hours',
    category: 'timesheet',
    valueType: 'number',
    supportedOperators: ['equals', 'notEquals', 'greaterThan', 'lessThan', 'greaterThanOrEqual', 'lessThanOrEqual'],
    exampleValue: 5
  },
  {
    field: 'timesheet.projectCount',
    label: 'Number of Projects',
    description: 'Count of unique projects in timesheet',
    category: 'timesheet',
    valueType: 'number',
    supportedOperators: ['equals', 'notEquals', 'greaterThan', 'lessThan', 'greaterThanOrEqual', 'lessThanOrEqual'],
    exampleValue: 3
  },
  {
    field: 'timesheet.allProjectsApproved',
    label: 'All Projects Approved',
    description: 'Whether all projects in timesheet are approved',
    category: 'timesheet',
    valueType: 'boolean',
    supportedOperators: ['isTrue', 'isFalse'],
    exampleValue: true
  },
  {
    field: 'timesheet.hasWeekendWork',
    label: 'Has Weekend Work',
    description: 'Whether timesheet includes Saturday or Sunday hours',
    category: 'timesheet',
    valueType: 'boolean',
    supportedOperators: ['isTrue', 'isFalse'],
    exampleValue: false
  },
  {
    field: 'timesheet.billableHours',
    label: 'Billable Hours',
    description: 'Total billable hours in timesheet',
    category: 'timesheet',
    valueType: 'number',
    supportedOperators: ['equals', 'notEquals', 'greaterThan', 'lessThan', 'greaterThanOrEqual', 'lessThanOrEqual'],
    exampleValue: 35
  },
  {
    field: 'timesheet.nonBillableHours',
    label: 'Non-Billable Hours',
    description: 'Total non-billable hours in timesheet',
    category: 'timesheet',
    valueType: 'number',
    supportedOperators: ['equals', 'notEquals', 'greaterThan', 'lessThan', 'greaterThanOrEqual', 'lessThanOrEqual'],
    exampleValue: 5
  },

  // Historical Fields
  {
    field: 'employee.consecutiveCleanSubmissions',
    label: 'Consecutive Clean Submissions',
    description: 'Number of consecutive timesheets approved without issues',
    category: 'historical',
    valueType: 'number',
    supportedOperators: ['equals', 'notEquals', 'greaterThan', 'lessThan', 'greaterThanOrEqual', 'lessThanOrEqual'],
    exampleValue: 10
  },
  {
    field: 'employee.hasViolationsInLast30Days',
    label: 'Has Violations (Last 30 Days)',
    description: 'Whether employee has any rejected timesheets in last 30 days',
    category: 'historical',
    valueType: 'boolean',
    supportedOperators: ['isTrue', 'isFalse'],
    exampleValue: false
  },
  {
    field: 'employee.hasViolationsInLast60Days',
    label: 'Has Violations (Last 60 Days)',
    description: 'Whether employee has any rejected timesheets in last 60 days',
    category: 'historical',
    valueType: 'boolean',
    supportedOperators: ['isTrue', 'isFalse'],
    exampleValue: false
  },
  {
    field: 'employee.hasViolationsInLast90Days',
    label: 'Has Violations (Last 90 Days)',
    description: 'Whether employee has any rejected timesheets in last 90 days',
    category: 'historical',
    valueType: 'boolean',
    supportedOperators: ['isTrue', 'isFalse'],
    exampleValue: false
  },
  {
    field: 'employee.averageWeeklyHours',
    label: 'Average Weekly Hours',
    description: 'Employee\'s average weekly hours over last 12 weeks',
    category: 'historical',
    valueType: 'number',
    supportedOperators: ['equals', 'notEquals', 'greaterThan', 'lessThan', 'greaterThanOrEqual', 'lessThanOrEqual'],
    exampleValue: 40
  },

  // Time-based Fields
  {
    field: 'timesheet.submittedOnWeekend',
    label: 'Submitted on Weekend',
    description: 'Whether timesheet was submitted on Saturday or Sunday',
    category: 'time',
    valueType: 'boolean',
    supportedOperators: ['isTrue', 'isFalse'],
    exampleValue: false
  },
  {
    field: 'timesheet.daysSinceSubmission',
    label: 'Days Since Submission',
    description: 'Number of days since timesheet was submitted',
    category: 'time',
    valueType: 'number',
    supportedOperators: ['equals', 'notEquals', 'greaterThan', 'lessThan', 'greaterThanOrEqual', 'lessThanOrEqual'],
    exampleValue: 2
  }
];

// ============================================================================
// Operator Definitions
// ============================================================================

export const OPERATOR_LABELS: Record<RuleOperator, string> = {
  equals: 'is',
  notEquals: 'is not',
  greaterThan: 'is greater than',
  lessThan: 'is less than',
  greaterThanOrEqual: 'is greater than or equal to',
  lessThanOrEqual: 'is less than or equal to',
  contains: 'contains',
  notContains: 'does not contain',
  in: 'is one of',
  notIn: 'is not one of',
  isTrue: 'is true',
  isFalse: 'is false'
};

// ============================================================================
// Action Definitions
// ============================================================================

export const RULE_ACTIONS: ActionMetadata[] = [
  {
    type: 'auto-approve',
    label: 'Auto-Approve',
    description: 'Automatically approve and advance to next stage',
    requiresParameters: false
  },
  {
    type: 'auto-advance',
    label: 'Auto-Advance',
    description: 'Skip this stage and move to next stage',
    requiresParameters: false
  },
  {
    type: 'require-review',
    label: 'Require Manual Review',
    description: 'Flag for manual review (prevents auto-processing)',
    requiresParameters: false
  },
  {
    type: 'add-note',
    label: 'Add Note',
    description: 'Add a note to the timesheet',
    requiresParameters: true,
    parameterFields: [
      {
        name: 'note',
        label: 'Note Text',
        type: 'textarea',
        required: true
      }
    ]
  },
  {
    type: 'notify',
    label: 'Send Notification',
    description: 'Send notification to specific people or roles',
    requiresParameters: true,
    parameterFields: [
      {
        name: 'notifyUserIds',
        label: 'Notify Users',
        type: 'multiselect',
        required: false
      },
      {
        name: 'notifyRoles',
        label: 'Notify Roles',
        type: 'multiselect',
        required: false,
        options: [
          { value: 'manager', label: 'Manager' },
          { value: 'hr', label: 'HR' },
          { value: 'finance', label: 'Finance' },
          { value: 'admin', label: 'Admin' }
        ]
      }
    ]
  },
  {
    type: 'flag',
    label: 'Add Flag',
    description: 'Add a visual flag or tag to the timesheet',
    requiresParameters: true,
    parameterFields: [
      {
        name: 'flagLabel',
        label: 'Flag Label',
        type: 'text',
        required: true
      },
      {
        name: 'flagColor',
        label: 'Flag Color',
        type: 'select',
        required: false,
        options: [
          { value: 'blue', label: 'Blue' },
          { value: 'green', label: 'Green' },
          { value: 'yellow', label: 'Yellow' },
          { value: 'orange', label: 'Orange' },
          { value: 'red', label: 'Red' },
          { value: 'purple', label: 'Purple' }
        ]
      }
    ]
  }
];

// ============================================================================
// Rule Templates
// ============================================================================

export const RULE_TEMPLATES: RuleTemplate[] = [
  {
    id: 'template-standard-hours',
    name: 'Auto-Approve Standard Hours',
    description: 'Automatically approve timesheets that match contracted hours exactly',
    category: 'common',
    template: {
      name: 'Auto-Approve Standard Hours',
      description: 'Automatically approve when total hours equal contracted hours',
      isEnabled: true,
      priority: 5,
      stopOnMatch: true,
      conditionGroup: {
        id: 'group-1',
        operator: 'AND',
        conditions: [
          {
            id: 'cond-1',
            field: 'timesheet.totalHours',
            operator: 'equals',
            value: 'timesheet.contractedHours',
            displayText: 'Total hours equals contracted hours'
          },
          {
            id: 'cond-2',
            field: 'timesheet.allProjectsApproved',
            operator: 'isTrue',
            value: true,
            displayText: 'All projects are approved'
          }
        ]
      },
      actions: [
        {
          id: 'action-1',
          type: 'auto-approve',
          parameters: {}
        },
        {
          id: 'action-2',
          type: 'add-note',
          parameters: {
            note: 'Auto-approved: Standard hours with approved projects'
          }
        }
      ]
    }
  },
  {
    id: 'template-trusted-employee',
    name: 'Fast-Track Trusted Employees',
    description: 'Auto-approve timesheets from employees with 10+ consecutive clean submissions',
    category: 'common',
    template: {
      name: 'Fast-Track Trusted Employees',
      description: 'Auto-approve for employees with excellent track record',
      isEnabled: true,
      priority: 3,
      stopOnMatch: true,
      conditionGroup: {
        id: 'group-1',
        operator: 'AND',
        conditions: [
          {
            id: 'cond-1',
            field: 'employee.consecutiveCleanSubmissions',
            operator: 'greaterThanOrEqual',
            value: 10,
            displayText: 'Employee has 10+ consecutive clean submissions'
          },
          {
            id: 'cond-2',
            field: 'timesheet.hasWeekendWork',
            operator: 'isFalse',
            value: false,
            displayText: 'No weekend work'
          }
        ]
      },
      actions: [
        {
          id: 'action-1',
          type: 'auto-approve',
          parameters: {}
        },
        {
          id: 'action-2',
          type: 'add-note',
          parameters: {
            note: 'Auto-approved: Trusted employee with excellent track record'
          }
        }
      ]
    }
  },
  {
    id: 'template-flag-overtime',
    name: 'Flag Overtime for Review',
    description: 'Require manual review for timesheets with overtime hours',
    category: 'common',
    template: {
      name: 'Flag Overtime for Review',
      description: 'Require manual review when overtime is present',
      isEnabled: true,
      priority: 2,
      stopOnMatch: true,
      conditionGroup: {
        id: 'group-1',
        operator: 'AND',
        conditions: [
          {
            id: 'cond-1',
            field: 'timesheet.overtimeHours',
            operator: 'greaterThan',
            value: 0,
            displayText: 'Overtime hours greater than 0'
          }
        ]
      },
      actions: [
        {
          id: 'action-1',
          type: 'require-review',
          parameters: {}
        },
        {
          id: 'action-2',
          type: 'flag',
          parameters: {
            flagLabel: 'Overtime',
            flagColor: 'orange'
          }
        },
        {
          id: 'action-3',
          type: 'notify',
          parameters: {
            notifyRoles: ['manager', 'hr']
          }
        }
      ]
    }
  },
  {
    id: 'template-department-specific',
    name: 'Department-Specific Auto-Approval',
    description: 'Auto-approve for specific departments with standard hours',
    category: 'common',
    template: {
      name: 'Engineering Department Auto-Approval',
      description: 'Auto-approve Engineering timesheets with standard hours',
      isEnabled: true,
      priority: 5,
      stopOnMatch: true,
      conditionGroup: {
        id: 'group-1',
        operator: 'AND',
        conditions: [
          {
            id: 'cond-1',
            field: 'employee.department',
            operator: 'equals',
            value: 'Engineering',
            displayText: 'Department is Engineering'
          },
          {
            id: 'cond-2',
            field: 'timesheet.totalHours',
            operator: 'lessThanOrEqual',
            value: 40,
            displayText: 'Total hours ≤ 40'
          }
        ]
      },
      actions: [
        {
          id: 'action-1',
          type: 'auto-approve',
          parameters: {}
        }
      ]
    }
  },
  {
    id: 'template-weekend-alert',
    name: 'Weekend Work Alert',
    description: 'Notify HR when weekend work is detected',
    category: 'compliance',
    template: {
      name: 'Weekend Work Alert',
      description: 'Alert HR when weekend work is present',
      isEnabled: true,
      priority: 1,
      stopOnMatch: false,
      conditionGroup: {
        id: 'group-1',
        operator: 'AND',
        conditions: [
          {
            id: 'cond-1',
            field: 'timesheet.hasWeekendWork',
            operator: 'isTrue',
            value: true,
            displayText: 'Has weekend work'
          }
        ]
      },
      actions: [
        {
          id: 'action-1',
          type: 'notify',
          parameters: {
            notifyRoles: ['hr']
          }
        },
        {
          id: 'action-2',
          type: 'flag',
          parameters: {
            flagLabel: 'Weekend Work',
            flagColor: 'yellow'
          }
        }
      ]
    }
  }
];

// ============================================================================
// Helper Functions
// ============================================================================

export function getFieldMetadata(field: RuleConditionField): FieldMetadata | undefined {
  return RULE_FIELDS.find(f => f.field === field);
}

export function getActionMetadata(actionType: RuleActionType): ActionMetadata | undefined {
  return RULE_ACTIONS.find(a => a.type === actionType);
}

export function getFieldsByCategory(category: 'employee' | 'timesheet' | 'historical' | 'time'): FieldMetadata[] {
  return RULE_FIELDS.filter(f => f.category === category);
}

export function getOperatorLabel(operator: RuleOperator): string {
  return OPERATOR_LABELS[operator] || operator;
}

export function getTemplateById(templateId: string): RuleTemplate | undefined {
  return RULE_TEMPLATES.find(t => t.id === templateId);
}
